import React from 'react';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import { useMutationCount } from '../hooks/useMutationCount';
import { useSnapshot } from '../context/SnapshotContext';

// ── Icône Wi-Fi barré ─────────────────────────────────────────
function WifiOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      <line x1="1" y1="1" x2="23" y2="23"/>
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/>
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
      <path d="M10.71 5.05A16 16 0 0 1 22.56 9"/>
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
      <circle cx="12" cy="20" r="1" fill="currentColor" stroke="none"/>
    </svg>
  );
}

// ── Icône horloge (modifications en attente) ──────────────────
function ClockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}

/**
 * Bandeau affiché en haut de l'application selon le contexte :
 *
 * 1. Hors-ligne + modifications en attente
 *    → bandeau orange "Mode dégradé — X modification(s) en attente"
 *
 * 2. Hors-ligne seul (aucune modification)
 *    → bandeau orange "Mode dégradé — données du cache"
 *
 * 3. En ligne + modifications en attente (serveur était down)
 *    → bandeau bleu "X modification(s) en attente de synchronisation"
 *
 * 4. En ligne, aucune modification en attente
 *    → pas de bandeau
 */
export function OfflineBanner() {
  const isOffline             = useOfflineStatus();
  const pendingCount          = useMutationCount();
  const { setShowSyncPanel }  = useSnapshot();

  if (!isOffline && pendingCount === 0) return null;

  const plural  = n => n > 1 ? 's' : '';
  const pluralE = n => n > 1 ? 'ées' : 'ée';

  // Bouton cliquable "N modification(s)"
  const PendingBtn = () => pendingCount > 0 ? (
    <button
      className="offline-banner__pending-btn"
      onClick={() => setShowSyncPanel(true)}
      title="Voir et synchroniser les modifications en attente"
    >
      {pendingCount} modification{plural(pendingCount)} en attente ↗
    </button>
  ) : null;

  // ── En ligne mais mutations en attente (serveur était down) ──
  if (!isOffline && pendingCount > 0) {
    return (
      <div className="offline-banner offline-banner--pending" role="status">
        <ClockIcon />
        <span>
          <strong>{pendingCount} modification{plural(pendingCount)}</strong>
          {' '}en attente de synchronisation.{' '}
          <PendingBtn />
        </span>
      </div>
    );
  }

  // ── Hors-ligne ────────────────────────────────────────────────
  return (
    <div className="offline-banner" role="alert" aria-live="polite">
      <WifiOffIcon />
      <span>
        <strong>Mode dégradé</strong>
        {' '}— Serveur inaccessible. Données du cache local.
        {pendingCount > 0 && (
          <> <PendingBtn /></>
        )}
      </span>
    </div>
  );
}
